(() => {
  if (customElements.get('recently-viewed-products')) return;

  const SELECTORS = {
    list: '[data-rv-role="list"]',
    empty: '[data-rv-role="empty"]',
    clear: '[data-rv-role="clear"]',
    template: 'template[data-rv-product-template]'
  };

  const responseCache = new Map();

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.list = this.querySelector(SELECTORS.list);
      this.emptyState = this.querySelector(SELECTORS.empty);
      this.clearButton = this.querySelector(SELECTORS.clear);
      this.controller = new AbortController();
      this.settings = this.readSettings();

      this.clearButton?.addEventListener('click', () => this.clearHistory());
      this.initialize();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    readSettings() {
      return {
        sectionId: this.dataset.sectionId || '',
        storageKey: this.dataset.storageKey || 'shopify:recently-viewed:v1',
        maxProducts: this.toPositiveInteger(this.dataset.maxProducts, 4),
        historyLimit: this.toPositiveInteger(this.dataset.historyLimit, 20),
        retentionDays: this.toPositiveInteger(this.dataset.retentionDays, 30),
        excludeCurrent: this.dataset.excludeCurrent === 'true',
        showEmptyState: this.dataset.showEmptyState === 'true',
        currentHandle: this.normalizeHandle(this.dataset.currentHandle)
      };
    }

    async initialize() {
      const history = this.readHistory();
      const visibleHistory = history
        .filter((entry) => !this.settings.excludeCurrent || entry.handle !== this.settings.currentHandle)
        .slice(0, this.settings.maxProducts);

      await this.render(visibleHistory);

      if (this.settings.currentHandle) {
        this.writeHistory([
          { handle: this.settings.currentHandle, viewedAt: Date.now() },
          ...history.filter((entry) => entry.handle !== this.settings.currentHandle)
        ]);
      }
    }

    readHistory() {
      try {
        const stored = JSON.parse(window.localStorage.getItem(this.settings.storageKey) || '[]');
        if (!Array.isArray(stored)) return [];

        const oldestAllowed = Date.now() - this.settings.retentionDays * 86400000;
        const uniqueHandles = new Set();

        return stored
          .map((entry) => {
            if (typeof entry === 'string') return { handle: this.normalizeHandle(entry), viewedAt: Date.now() };
            return {
              handle: this.normalizeHandle(entry?.handle),
              viewedAt: Number(entry?.viewedAt || 0)
            };
          })
          .filter((entry) => {
            if (!entry.handle || entry.viewedAt < oldestAllowed || uniqueHandles.has(entry.handle)) return false;
            uniqueHandles.add(entry.handle);
            return true;
          })
          .slice(0, this.settings.historyLimit);
      } catch (error) {
        return [];
      }
    }

    writeHistory(history) {
      try {
        window.localStorage.setItem(
          this.settings.storageKey,
          JSON.stringify(history.slice(0, this.settings.historyLimit))
        );
      } catch (error) {
        // Browsing history is optional when storage is unavailable or blocked.
      }
    }

    async render(history) {
      if (!this.list) return;

      if (!history.length) {
        this.finishRendering([]);
        return;
      }

      const results = await Promise.all(
        history.map(async (entry) => ({ entry, card: await this.fetchCard(entry.handle) }))
      );
      const cards = results.map((result) => result.card).filter(Boolean);
      const failedHandles = new Set(results.filter((result) => !result.card).map((result) => result.entry.handle));

      this.list.replaceChildren(...cards);
      this.finishRendering(cards);

      if (failedHandles.size) {
        this.writeHistory(this.readHistory().filter((entry) => !failedHandles.has(entry.handle)));
      }
    }

    async fetchCard(handle) {
      const root = window.Shopify?.routes?.root || '/';
      const productUrl = `${root}products/${encodeURIComponent(handle)}`;
      const requestUrl = `${productUrl}?section_id=${encodeURIComponent(this.settings.sectionId)}`;

      try {
        let htmlPromise = responseCache.get(requestUrl);
        if (!htmlPromise) {
          htmlPromise = fetch(requestUrl, {
            headers: { Accept: 'text/html' },
            signal: this.controller.signal
          }).then((response) => {
            if (!response.ok) throw new Error(`Recently viewed request failed: ${response.status}`);
            return response.text();
          });
          responseCache.set(requestUrl, htmlPromise);
        }

        const html = await htmlPromise;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const template = documentFragment.querySelector(SELECTORS.template);
        const card = template?.content?.firstElementChild;
        return card ? document.importNode(card, true) : null;
      } catch (error) {
        responseCache.delete(requestUrl);
        if (error.name !== 'AbortError') console.warn(error.message);
        return null;
      }
    }

    finishRendering(cards) {
      const hasCards = cards.length > 0;
      this.list?.setAttribute('aria-busy', 'false');
      if (this.emptyState) this.emptyState.hidden = hasCards || !this.settings.showEmptyState;
      if (this.clearButton) this.clearButton.hidden = !hasCards;
      this.hidden = !hasCards && !this.settings.showEmptyState;
    }

    clearHistory() {
      try {
        window.localStorage.removeItem(this.settings.storageKey);
      } catch (error) {
        // The visual state can still be cleared if browser storage is blocked.
      }
      this.list?.replaceChildren();
      this.finishRendering([]);
    }

    normalizeHandle(value) {
      return String(value || '')
        .trim()
        .replace(/^.*\/products\//, '')
        .split(/[?#/]/)[0]
        .toLowerCase();
    }

    toPositiveInteger(value, fallback) {
      const number = Number.parseInt(value, 10);
      return Number.isFinite(number) && number > 0 ? number : fallback;
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
})();
